/* eslint-disable no-console */
import {SearchOutlined} from '@ant-design/icons';
import {Table as TableAnt, InputRef, Input} from 'antd';
import type {ColumnsType, TableProps} from 'antd/es/table';
import {ColumnType} from 'antd/es/table';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {ReactComponent as NoDataIcon} from '../../../assets/icons/noDataIcon.svg';
import {emissionUnits} from '../../../components/Forms/interface/unitChange';
import Loader from '../../../components/Loader/Loader';
import NoTableData from '../../../components/NoData/NoTableData';
import {defaultCol} from '../../../components/Table/Interface/ITable';
import {debounce} from '../../../utils/debounce';
import {formatIndian} from '../../../utils/formatIndian';

import {toDecimalString} from './common';
import {DataLineageLabel, DataLineageKey} from './DataLineageKeyLabel';
import type {
  DataLineageColumnSearch,
  DataLineageRow,
  DataLineageSort,
  DataLineageSortBy
} from './dataLineageTypes';

type ColumnSearchField = keyof DataLineageColumnSearch;

const SORTABLE_COLUMNS: Record<string, DataLineageSortBy> = {
  [DataLineageKey.INPUT_DATA]: 'inputData',
  [DataLineageKey.SI_DATA]: 'siData',
  [DataLineageKey.YEARLY_EMISSIONS]: 'yearlyEmissions'
};

const ColumnSearchInput: React.FC<{
  field: ColumnSearchField;
  title: string;
  value?: string;
  onSearchChange: (field: ColumnSearchField, value: string) => void;
  inputRef: React.RefObject<InputRef>;
  onDraftChange: (value: string) => void;
  onConfirm: () => void;
}> = ({
  field,
  title,
  value,
  onSearchChange,
  inputRef,
  onDraftChange,
  onConfirm
}) => {
  const [draft, setDraft] = useState(value ?? '');
  const latestSearchRef = useRef({field, value: value ?? ''});
  const debouncedSearchRef = useRef(
    debounce(() => {
      const {field: searchField, value: searchValue} = latestSearchRef.current;
      onSearchChange(searchField, searchValue);
    }, 400)
  );

  useEffect(() => {
    const nextValue = value ?? '';
    setDraft(nextValue);
    latestSearchRef.current = {field, value: nextValue};
  }, [field, value]);

  return (
    <Input
      ref={inputRef}
      value={draft}
      placeholder={`Search ${title}`}
      onChange={(e) => {
        const nextValue = e.target.value;
        setDraft(nextValue);
        onDraftChange(nextValue);
        latestSearchRef.current = {field, value: nextValue};

        if (!nextValue) {
          onSearchChange(field, nextValue);

          return;
        }

        debouncedSearchRef.current();
      }}
      onPressEnter={onConfirm}
      allowClear
    />
  );
};

const DataLineageTable: React.FC<{
  records: DataLineageRow[];
  loading: boolean;
  pagination: {current: number; pageSize: number; total: number};
  sort: DataLineageSort;
  columnSearch: DataLineageColumnSearch;
  onPaginationChange: (page: number, pageSize: number) => void;
  onSortChange: (
    sortBy?: DataLineageSortBy,
    sortOrder?: 'asc' | 'desc'
  ) => void;
  onColumnSearchChange: (field: ColumnSearchField, value: string) => void;
}> = ({
  records,
  loading,
  pagination,
  sort,
  columnSearch,
  onPaginationChange,
  onSortChange,
  onColumnSearchChange
}) => {
  const searchInput = useRef<InputRef>(null);

  const getSortOrder = (
    columnKey: DataLineageSortBy
  ): 'ascend' | 'descend' | null => {
    if (sort.sortBy !== columnKey) {
      return null;
    }

    return sort.sortOrder === 'desc' ? 'descend' : 'ascend';
  };

  const SearchFilter = useCallback(
    (
      title: string,
      field: ColumnSearchField
    ): defaultCol & ColumnType<DataLineageRow> => {
      const searchValue = columnSearch[field];

      return {
        filterDropdown: ({setSelectedKeys, confirm}) => {
          return (
            <div
              style={{padding: 8}}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
            >
              <ColumnSearchInput
                field={field}
                title={title}
                value={searchValue}
                onSearchChange={onColumnSearchChange}
                inputRef={searchInput}
                onDraftChange={(draftValue) => {
                  setSelectedKeys(draftValue ? [draftValue] : []);
                }}
                onConfirm={confirm}
              />
            </div>
          );
        },
        filterIcon: () => (
          <SearchOutlined
            style={{color: searchValue ? '#1677ff' : undefined}}
          />
        ),
        filteredValue: searchValue ? [searchValue] : null,
        onFilterDropdownOpenChange: (visible) => {
          if (visible) {
            setTimeout(() => searchInput.current?.select(), 100);
          }
        }
      };
    },
    [columnSearch, onColumnSearchChange]
  );

  const columns = useMemo<ColumnsType<DataLineageRow>>(
    () => [
      {
        title: DataLineageLabel.GROUP,
        dataIndex: DataLineageKey.GROUP,
        ...SearchFilter(DataLineageLabel.GROUP, 'group')
      },
      {
        title: DataLineageLabel.INPUT_DATA,
        dataIndex: DataLineageKey.INPUT_DATA,
        key: DataLineageKey.INPUT_DATA,
        sorter: true,
        sortOrder: getSortOrder('inputData'),
        render: (inputData: string) => inputData
      },
      {
        title: DataLineageLabel.SI_DATA,
        dataIndex: DataLineageKey.SI_DATA,
        key: DataLineageKey.SI_DATA,
        sorter: true,
        sortOrder: getSortOrder('siData'),
        render: (inputData: string) => inputData
      },
      {
        title: DataLineageLabel.EF_DATA,
        dataIndex: DataLineageKey.EF_DATA,
        key: DataLineageKey.EMISSION_FACTOR,
        render: (_: unknown, record: DataLineageRow) => {
          const eFData = record.eFData;
          const emissionFactor = eFData?.emissionFactor;
          const efUnit = eFData?.efUnit;
          const hasEmissionFactor =
            emissionFactor !== undefined &&
            emissionFactor !== null &&
            emissionFactor !== '' &&
            Number.isFinite(Number(emissionFactor));

          return hasEmissionFactor && efUnit
            ? eFData?.leakageFactor
              ? `x ${toDecimalString(
                  Number(eFData.leakageFactor)
                )} % Leakage x ${toDecimalString(
                  Number(eFData.emissionFactor)
                )} ${efUnit}`
              : `x ${toDecimalString(Number(eFData.emissionFactor))} ${efUnit}`
            : '-';
        }
      },
      {
        title: DataLineageLabel.URL_NAME,
        dataIndex: DataLineageKey.EF_DATA,
        key: DataLineageKey.EMISSIONS_FACTOR_SOURCE,
        ...SearchFilter(DataLineageLabel.URL_NAME, 'urlName'),
        render: (_: unknown, record: DataLineageRow) => {
          const eFData = record.eFData;

          return eFData?.url ? (
            <a href={eFData.url} target="_blank" rel="noopener noreferrer">
              {eFData.urlName}
            </a>
          ) : (
            eFData?.urlName ?? '-'
          );
        }
      },
      {
        title: DataLineageLabel.YEARLY_EMISSIONS,
        dataIndex: DataLineageKey.YEARLY_EMISSIONS,
        key: DataLineageKey.YEARLY_EMISSIONS,
        sorter: true,
        sortOrder: getSortOrder('yearlyEmissions'),
        render: (yearlyEmissions: number | string | undefined) =>
          `${formatIndian(yearlyEmissions ?? 0)} ${emissionUnits.T_CO2e}`
      }
    ],
    [SearchFilter, sort.sortBy, sort.sortOrder]
  );

  const handleTableChange: TableProps<DataLineageRow>['onChange'] = (
    paginationInfo,
    _filters,
    sorter,
    extra
  ) => {
    if (extra?.action === 'paginate') {
      if (paginationInfo.current && paginationInfo.pageSize) {
        onPaginationChange(paginationInfo.current, paginationInfo.pageSize);
      }

      return;
    }

    if (extra?.action === 'sort') {
      const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
      const columnKey = sorterResult?.columnKey ?? sorterResult?.field;

      if (!sorterResult?.order) {
        onSortChange(undefined, undefined);

        return;
      }

      const sortBy =
        typeof columnKey === 'string' ? SORTABLE_COLUMNS[columnKey] : undefined;

      if (sortBy) {
        onSortChange(sortBy, sorterResult.order === 'descend' ? 'desc' : 'asc');
      }
    }
  };

  return (
    <div>
      <TableAnt
        dataSource={records}
        columns={columns}
        loading={{
          spinning: loading,
          indicator: <Loader isHome={true} />
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        onChange={handleTableChange}
        rowKey={(record, index) =>
          record._id ?? `${String(record.group ?? '')}-${index}`
        }
        locale={{
          emptyText: (
            <NoTableData
              icon={<NoDataIcon />}
              title="No carbon accounting data found for this organization."
            />
          )
        }}
      />
    </div>
  );
};

export default DataLineageTable;
